import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Download, Eye, Key, Mail, PackageSearch, RefreshCw, Search, Send } from "lucide-react";
import { AdminTitle, Status } from "@/components/admin/admin-ui";
import { Button } from "@/components/ui/button";
import { meta } from "@/components/store/page-parts";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/orders")({
  head: () => meta("Orders — BetterAccess Admin", "Review payment, digital fulfilment status, and delivered credentials."),
  component: Orders,
});

function Orders() {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Selected Order Modal state
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [customKeys, setCustomKeys] = useState("");
  const [fulfilling, setFulfilling] = useState(false);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/orders");
      if (res.ok) {
        const d = await res.json();
        if (Array.isArray(d.orders)) setOrders(d.orders);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchOrders();
  }, []);

  const handleRefulfill = async () => {
    if (!selectedOrder) return;
    setFulfilling(true);
    try {
      const payload: any = { orderId: selectedOrder.id };
      if (customKeys.trim()) {
        payload.customDeliveredData = customKeys.trim().split("\n").filter(Boolean);
      }
      const res = await fetch("/api/admin/orders/refulfill", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || "Fulfillment failed");
      toast.success("Order fulfilled and delivery email sent to customer!");
      setSelectedOrder(null);
      setCustomKeys("");
      await fetchOrders();
    } catch (err: any) {
      toast.error(err.message || "Could not fulfill order");
    } finally {
      setFulfilling(false);
    }
  };

  const handleExportCsv = () => {
    if (!orders.length) return;
    const headers = ["Order ID", "Customer Email", "Product", "Quantity", "Total", "Currency", "Gateway", "Payment Status", "Fulfillment Status", "Date"];
    const rows = orders.map(o => [
      o.id,
      `"${o.customer_email || ""}"`,
      `"${o.product_name || ""}"`,
      o.quantity || 1,
      o.total_amount || 0,
      o.currency || "BDT",
      o.payment_gateway || "",
      o.payment_status || "",
      o.fulfillment_status || "",
      o.created_at || "",
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `orders_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const visible = orders.filter(o => {
    const matchSearch = `${o.id} ${o.customer_email} ${o.product_name}`.toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      statusFilter === "all"
        ? true
        : statusFilter === "delivered"
        ? o.fulfillment_status === "delivered"
        : statusFilter === "paid"
        ? o.payment_status === "paid"
        : statusFilter === "pending"
        ? o.payment_status === "pending"
        : true;
    return matchSearch && matchStatus;
  });

  return (
    <>
      <AdminTitle
        eyebrow="Fulfilment"
        title="Orders &amp; Deliveries"
        copy="Track every customer transaction, verify gateway status and dispatch digital credentials."
        action={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchOrders} disabled={loading}>
              <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={!orders.length}>
              <Download className="size-3.5" /> Export CSV
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex max-w-md items-center gap-2 rounded-md border bg-card px-3 flex-1">
          <Search className="size-4 text-muted-foreground" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-10 flex-1 bg-transparent text-sm outline-none"
            placeholder="Search order ID, customer email..."
          />
        </div>

        <div className="flex gap-1.5 overflow-x-auto">
          {["all", "paid", "delivered", "pending"].map(st => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`rounded-md px-3 py-1.5 text-xs font-bold capitalize transition ${
                statusFilter === st ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
              }`}
            >
              {st}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full min-w-[700px] text-left text-sm">
          <thead className="border-b bg-secondary/60 text-[10px] font-extrabold uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Order ID</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Gateway</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Payment</th>
              <th className="px-4 py-3">Delivery</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {visible.map(o => (
              <tr key={o.id} className="transition hover:bg-secondary/40">
                <td className="px-4 py-3 font-mono font-bold">{o.id}</td>
                <td className="px-4 py-3">
                  <span className="block font-medium">{o.customer_email}</span>
                  {o.customer_phone && <span className="text-xs text-muted-foreground">{o.customer_phone}</span>}
                </td>
                <td className="px-4 py-3 font-semibold">{o.product_name} (x{o.quantity})</td>
                <td className="px-4 py-3 text-xs uppercase font-mono">{o.payment_gateway}</td>
                <td className="px-4 py-3 font-mono font-bold">
                  {o.currency === "USD" ? `$${o.total_amount?.toFixed(2)}` : `৳${o.total_amount?.toFixed(0)}`}
                </td>
                <td className="px-4 py-3">
                  <Status>{o.payment_status === "paid" ? "Paid" : o.payment_status}</Status>
                </td>
                <td className="px-4 py-3">
                  <Status>{o.fulfillment_status === "delivered" ? "Delivered" : o.fulfillment_status}</Status>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setSelectedOrder(o)}>
                    <Eye className="size-4" /> Manage
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Order Details & Refulfill Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl border bg-card p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b pb-4">
              <div>
                <span className="font-mono text-xs font-bold text-primary">ORDER DETAILS</span>
                <h2 className="text-xl font-black">{selectedOrder.id}</h2>
              </div>
              <Button size="sm" variant="outline" onClick={() => setSelectedOrder(null)}>
                Close
              </Button>
            </div>

            <div className="mt-4 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3 rounded-lg bg-secondary/50 p-3 text-xs">
                <div>
                  <span className="text-muted-foreground">Customer:</span>
                  <p className="font-bold">{selectedOrder.customer_email}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Product:</span>
                  <p className="font-bold">{selectedOrder.product_name} (x{selectedOrder.quantity})</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Payment Gateway:</span>
                  <p className="font-bold uppercase font-mono">{selectedOrder.payment_gateway}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Amount:</span>
                  <p className="font-bold">
                    {selectedOrder.currency === "USD" ? `$${selectedOrder.total_amount}` : `৳${selectedOrder.total_amount}`}
                  </p>
                </div>
              </div>

              {selectedOrder.delivered_data && (
                <div>
                  <span className="text-xs font-bold text-foreground">Delivered Credentials:</span>
                  <pre className="mt-1 max-h-32 overflow-y-auto rounded-md bg-ink p-3 font-mono text-xs text-ink-foreground whitespace-pre-wrap">
                    {typeof selectedOrder.delivered_data === "string"
                      ? selectedOrder.delivered_data
                      : JSON.stringify(selectedOrder.delivered_data, null, 2)}
                  </pre>
                </div>
              )}

              <div className="rounded-lg border bg-card p-4">
                <p className="text-xs font-extrabold uppercase text-primary">Dispatch / Deliver Credentials</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Paste custom keys or license codes below (one per line). Leaving it empty will auto-allocate from available product stock.
                </p>
                <textarea
                  rows={3}
                  value={customKeys}
                  onChange={e => setCustomKeys(e.target.value)}
                  placeholder="Optional custom credentials or license key..."
                  className="mt-2 w-full rounded-md border bg-background p-2.5 font-mono text-xs outline-none focus:ring-2 focus:ring-primary"
                />
                <Button className="mt-3 w-full font-bold" onClick={handleRefulfill} disabled={fulfilling}>
                  {fulfilling ? (
                    <span className="flex items-center gap-2">
                      <RefreshCw className="size-4 animate-spin" /> Fulfilling &amp; Sending Email...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="size-4" /> Deliver Credentials &amp; Email Customer
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}